import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { env } from '@shared/config/env';
import { toApiError } from '@shared/api/api-error';
import { tokenStorage } from '@shared/api/token-storage';

/** The backend's success envelope. */
interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

/**
 * Called when the server rejects our token. The auth store registers itself here
 * at startup — an inversion that lets this module sign the user out without
 * importing the store, which would otherwise create an import cycle.
 */
type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler = () => {};

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use((config) => {
  const stored = tokenStorage.read();

  if (stored) {
    config.headers.set('Authorization', `Bearer ${stored.accessToken}`);
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const apiError = toApiError(error);

    // There is no refresh flow: an expired or rejected token means the session
    // is over, so clear it and let the router send the admin back to /login.
    if (apiError.isUnauthorized) {
      tokenStorage.clear();
      onUnauthorized();
    }

    return Promise.reject(apiError);
  },
);

/**
 * Thin typed wrapper that unwraps the `{ data }` envelope, so callers work with
 * domain shapes and never see the transport format.
 */
export const http = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.get<Envelope<T>>(url, config);
    return response.data.data;
  },

  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.post<Envelope<T>>(url, body, config);
    return response.data.data;
  },

  async patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.patch<Envelope<T>>(url, body, config);
    return response.data.data;
  },

  async put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.put<Envelope<T>>(url, body, config);
    return response.data.data;
  },

  /** For 204 responses, which carry no envelope to unwrap. */
  async delete(url: string, config?: AxiosRequestConfig): Promise<void> {
    await axiosInstance.delete(url, config);
  },
};
